/** @format */

import React, {useCallback, useContext, useEffect} from 'react';

import {QuoteContext} from 'frontier-quoter';
import {Radio, Input, Select, GridSelect, Icon, Checkbox, Tooltip} from '@sttark/design-system';

import {
    REGULATORY_INDUSTRY_NOTAPPLICABLE,
    GRAPHIC_DESIGN_ORDER_TYPE_ARTWORK_EDITS,
    GRAPHIC_DESIGN_ORDER_TYPE_CUSTOM_DESIGN,
    PRODUCT_TYPE_CARTON,
    PRODUCT_TYPE_LABEL,
    DESIGN_QUOTER_STEP_LANDING,
    QUOTER_SHAPE_PREVIOUSLY_USED,
    GRAPHIC_DESIGN_ORDER_TYPE_ARTWORK_RECREATION,
    SIZE_TYPE_STANDARD,
    CARTON_UNLAMINATED_WHITE_PAPER,
    CARTON_BRIGHT_WHITE_PAPER,
    REGULATORY_INDUSTRY_TYPES,
    ORDER_STATUS_IN_CART,
    NO_LAMINATE_SUBSTRATES,
    LAMINATE_NO_LAMINATE,
    COLOR_CAPATIBILITY_MATRIX,
    COLOR_BLACK_ONLY,
    CARTON_WHITE_PAPER,
    CARTON_WHITE_PAPER_C2S,
    SHAPE_CARTON_AUTOLOCK
} from 'frontier-constants';

import {Header} from '../header/header';
import SizeSelect from '../size_select';
import MaterialSelect from '../material_select';
import ShapeSelect from '../shape_select';
import Section from '../section';
import {capitalize} from '../functions/format';
import {PreviouslyUsedShapeSelect} from '../previously_used_shape_select';
import useDieSelection from '../hooks/use_die_selection';
import CustomShapeSelect from '../custom_shape_select';
import CartonMaterialSelect from '../carton_material_select';
import CartonLaminateSelect from '../carton_laminate_select';

import DesignTypeHeading from './design_type_heading';
import GraphicDesignArtworkUploader from 'frontier/ds/quoter/design_quoter/artwork_uploader';
import AssetsUploader from 'frontier/ds/quoter/design_quoter/assets_uploader';
import OrderSummary from 'frontier/ds/quoter/design_quoter/order_summary';
import cartonBoxInfo from 'frontier/ds/quoter/assets/carton-box-autolock.svg';
import LaminateSelect from 'frontier/ds/quoter/laminate_select';
import {ConditionalButton} from 'frontier-utilities';
import cartonBoxAutolock from 'frontier/ds/quoter/assets/carton-box-autolock.svg';
import cartonBoxStraighTuck from 'frontier/ds/quoter/assets/carton-box-straight-tuck.svg';

export default function DesignQuoter() {
    const ctx = useContext(QuoteContext);
    const {order, designOrder} = ctx;
    const {errors} = ctx.handleIsComplete(order);

    const {
        shapeCategory,
        setShapeCategory,
        customShapeSubCategory,
        setCustomShapeSubCategory,
        corners,
        setCorners,
        width,
        setWidth,
        length,
        setLength,
        depth,
        setDepth,
        allDies,
        cartonDies,
        diesMatchingShape,
        selectedDie,
        isCustomShape,
        isNewDie
    } = useDieSelection({
        order: ctx.order,
        updateOrder: patch => ctx.handleChange({order: {$merge: patch}})
    });

    const isLabel = designOrder.design_product_type_id === PRODUCT_TYPE_LABEL;
    const isCarton = designOrder.design_product_type_id === PRODUCT_TYPE_CARTON;

    const canBlackInkOnly = COLOR_CAPATIBILITY_MATRIX[COLOR_BLACK_ONLY].includes(
        order.substrate_id
    );

    const handleMaterialChange = useCallback(
        v => ctx.handleChangeMaterial(v),
        [ctx.handleChangeMaterial]
    );

    const handleLaminateChange = isLabel
        ? useCallback(
              v => ctx.handleChange({order: {laminate_id: {$set: v}}}),
              [ctx.handleChange, ctx.handleChangeMaterial]
          )
        : useCallback(
              v => {
                  const updateTarget = {order: {laminate_id: {$set: v}}};
                  // In cartons we only show "white paper"
                  // Even when unlaminated white paper is a completely different substrate
                  if (
                      v === LAMINATE_NO_LAMINATE &&
                      (ctx.order.substrate_id === CARTON_BRIGHT_WHITE_PAPER ||
                          ctx.order.substrate_id === CARTON_WHITE_PAPER ||
                          ctx.order.substrate_id === CARTON_WHITE_PAPER_C2S)
                  ) {
                      updateTarget.order.substrate_id = {$set: CARTON_UNLAMINATED_WHITE_PAPER};
                  } else if (
                      v !== LAMINATE_NO_LAMINATE &&
                      ctx.order.substrate_id === CARTON_UNLAMINATED_WHITE_PAPER
                  ) {
                      updateTarget.order.substrate_id = {$set: CARTON_BRIGHT_WHITE_PAPER};
                  }
                  ctx.handleChange(updateTarget);
              },
              [ctx.handleChange, ctx.order.substrate_id, ctx.order.laminate_id]
          );

    const handleDieSelected = useCallback(
        v =>
            ctx.handleChange({
                order: {size_id: {$set: v}, size_type_id: {$set: SIZE_TYPE_STANDARD}}
            }),
        [ctx.handleChange]
    );

    const handleColorChange = (checked, color_id) => {
        if (checked) {
            ctx.handleChange({order: {color_id: {$set: color_id}}});
        } else if (!checked && order.color_id === color_id) {
            ctx.handleChange({order: {color_id: {$set: null}}});
        }
    };

    // A "custom shape" is a shape that isn't rectangular or circular, where we display a
    // specific list of dies from our inventory.
    const isSelectingExistingCustomShape = isCustomShape && !isNewDie;
    const isSelectingPreviouslyUsed = shapeCategory === QUOTER_SHAPE_PREVIOUSLY_USED;
    const shouldShowSizeInputs = !isSelectingExistingCustomShape && !isSelectingPreviouslyUsed;

    const isArtworkEdits = designOrder.design_type_id === GRAPHIC_DESIGN_ORDER_TYPE_ARTWORK_EDITS;
    const isCustomArtwork = designOrder.design_type_id === GRAPHIC_DESIGN_ORDER_TYPE_CUSTOM_DESIGN;
    const isArtworkRecreation =
        designOrder.design_type_id === GRAPHIC_DESIGN_ORDER_TYPE_ARTWORK_RECREATION;

    const isLaminateRequired = NO_LAMINATE_SUBSTRATES.indexOf(ctx.order.substrate_id) === -1;

    const enabledSections = {};
    enabledSections.laminate = !!order.substrate_id && isLaminateRequired;
    enabledSections.shape = true;
    enabledSections.size = enabledSections.shape && !!shapeCategory && allDies.length > 0;

    const selectedIndustry = REGULATORY_INDUSTRY_TYPES.find(
        i => i.id == designOrder.product_industry_id
    );

    // alert users their order will be lost if leaving page
    useEffect(() => {
        window.addEventListener('beforeunload', alertUser);
        if (order.status_id === ORDER_STATUS_IN_CART)
            window.removeEventListener('beforeunload', alertUser);

        return () => {
            window.removeEventListener('beforeunload', alertUser);
        };
    }, [ctx]);

    const alertUser = e => {
        e.preventDefault();
        e.returnValue = '';
    };

    return (
        <div className="container mt-3 mb-8">
            <Header />
            <div className="row g-lg-7">
                <div className="col-lg-8">
                    <DesignTypeHeading
                        designType={designOrder.design_type_id}
                        productType={designOrder.design_product_type_id}
                        onClickBack={() =>
                            ctx.handleSetDesignQuoterStep(DESIGN_QUOTER_STEP_LANDING)
                        }
                    />
                    <section>
                        <h3>Select Your {capitalize(ctx.singularProduct)} Material</h3>
                        {isLabel && (
                            <MaterialSelect
                                value={order.substrate_id}
                                onChange={handleMaterialChange}
                            />
                        )}
                        {isCarton && (
                            <CartonMaterialSelect
                                order={order}
                                onChange={handleMaterialChange.bind(this)}
                            />
                        )}
                    </section>
                    <Section visible={enabledSections.laminate}>
                        <h5>Laminate</h5>
                        <p>Select a laminate type</p>
                        <p>
                            *Choosing a laminate is optional for design orders. Skip this question
                            if you do not know your laminate yet.
                        </p>
                        {isLabel && (
                            <LaminateSelect
                                value={order.laminate_id}
                                onChange={handleLaminateChange}
                                substrate={order.substrate_id}
                            />
                        )}
                        {isCarton && (
                            <CartonLaminateSelect
                                order={order}
                                onChange={handleLaminateChange.bind(this)}
                            />
                        )}
                    </Section>
                    <Section enabled={enabledSections.shape}>
                        <ShapeSelect
                            shapeCategory={shapeCategory}
                            onShapeCategoryChange={setShapeCategory}
                            customShapeSubCategory={customShapeSubCategory}
                            onCustomShapeSubCategoryChange={setCustomShapeSubCategory}
                            corners={corners}
                            onCornersChange={setCorners}
                        />
                    </Section>
                    {isLabel && (
                        <>
                            <Section enabled={enabledSections.size}>
                                {isSelectingExistingCustomShape && diesMatchingShape.length > 0 && (
                                    <>
                                        <h4>Choose a Shape</h4>
                                        <CustomShapeSelect
                                            dies={diesMatchingShape}
                                            selectedDie={selectedDie}
                                            onChange={handleDieSelected}
                                        />
                                    </>
                                )}
                                {isSelectingPreviouslyUsed && (
                                    <>
                                        <h4>Previously Used Custom Shapes</h4>
                                        <PreviouslyUsedShapeSelect
                                            selectedDie={selectedDie}
                                            onChange={handleDieSelected}
                                        />
                                    </>
                                )}
                                {shouldShowSizeInputs && (
                                    <>
                                        <h3>
                                            Select Your Desired {capitalize(ctx.singularProduct)}{' '}
                                            Size
                                        </h3>
                                        <SizeSelect
                                            dies={diesMatchingShape}
                                            width={width}
                                            onWidthChange={setWidth}
                                            length={length}
                                            onLengthChange={setLength}
                                            errors={errors}
                                        />
                                    </>
                                )}
                            </Section>
                        </>
                    )}
                    {isCarton && (
                        <>
                            <h3>Select Your Desired Carton Size</h3>
                            <SizeSelect
                                dies={cartonDies}
                                width={width}
                                onWidthChange={setWidth}
                                length={length}
                                onLengthChange={setLength}
                                depth={depth}
                                onDepthChange={setDepth}
                                errors={errors}
                            />
                            <div className="text-center">
                                <ConditionalButton
                                    enabled={shapeCategory === SHAPE_CARTON_AUTOLOCK}>
                                    <img
                                        src={cartonBoxAutolock}
                                        alt="Carton Dimensions"
                                        className="w-100 mt-4"
                                    />
                                    <img
                                        src={cartonBoxStraighTuck}
                                        alt="Carton Dimensions"
                                        className="w-100 mt-4"
                                    />
                                </ConditionalButton>
                            </div>
                        </>
                    )}
                    <>
                        <h3>Color Selection</h3>
                        <p>If you have specific color preferences, please enable those below.</p>
                        <Checkbox
                            disabled={!canBlackInkOnly}
                            checked={order.color_id === COLOR_BLACK_ONLY}
                            onChange={b => handleColorChange(b, COLOR_BLACK_ONLY)}>
                            Black Ink Only
                            <Tooltip content="An economical print mode where artwork is converted to grayscale and only black ink is used.">
                                <Icon secondary small icon="info" />
                            </Tooltip>
                        </Checkbox>
                    </>
                    {isArtworkEdits && (
                        <>
                            <h3>Upload Artwork</h3>
                            <p>Be sure to upload the artwork for all designs that need editing.</p>
                            <GraphicDesignArtworkUploader />
                        </>
                    )}
                    {isArtworkRecreation && (
                        <>
                            <h3>Upload Artwork</h3>
                            <p>Be sure to upload all artwork that needs to be recreated.</p>
                            <GraphicDesignArtworkUploader />
                        </>
                    )}
                    {!isCustomArtwork && (
                        <>
                            <h3>Artwork Instructions</h3>
                            <div>
                                Provide any edits or changes you’d like made, including:
                                <ul style={{listStyle: 'inside'}}>
                                    <li>Color changes, text edits, resizing, etc.</li>
                                    <li>Color Builds and/or Pantones</li>
                                    <li>
                                        Fonts (Note: Additional charges may apply if we need to
                                        purchase a font license for your project)
                                    </li>
                                    <li>
                                        Custom shape requests (Note: Once your order is placed, a
                                        dieline creation charge will be applied to your account)
                                    </li>
                                </ul>
                            </div>
                            <Input
                                rows={6}
                                label="Notes about artwork"
                                value={designOrder.comments_graphic_design || ''}
                                onChange={v =>
                                    ctx.handleDesignOrderChange({
                                        comments_graphic_design: {$set: v}
                                        //comments_customer: {$set: v}
                                    })
                                }
                            />
                        </>
                    )}

                    <h3>Vector or High-Resolution Logo</h3>
                    {isArtworkEdits && (
                        <p>Does your file contain a high-resolution or vector logo?</p>
                    )}
                    {!isArtworkEdits && (
                        <p>Can you provide a vector or high-resolution logo file?</p>
                    )}
                    <div className="hstack">
                        <div className="me-4">
                            <Radio
                                label="Yes"
                                checked={designOrder.is_vector_logo === true}
                                value={true}
                                onChange={() =>
                                    ctx.handleDesignOrderChange({is_vector_logo: {$set: true}})
                                }
                            />
                        </div>
                        <div>
                            <Radio
                                label="No"
                                checked={designOrder.is_vector_logo === false}
                                value={false}
                                onChange={() =>
                                    ctx.handleDesignOrderChange({is_vector_logo: {$set: false}})
                                }
                            />
                        </div>
                    </div>
                    {designOrder.is_vector_logo === false && !isArtworkEdits && (
                        <p>
                            <strong>No problem!</strong> We'll recreate it for you, so your design
                            comes out crystal clear.
                        </p>
                    )}
                    {designOrder.is_vector_logo === true && !isArtworkEdits && (
                        <p>
                            <strong>Great!</strong>
                        </p>
                    )}
                    {!isArtworkEdits && <AssetsUploader uploadType="logo" />}

                    {!isCustomArtwork && (
                        <>
                            <h3>Additional Files</h3>
                            <p>
                                Do you have any other files that may be helpful for our designers?
                                Example: brand guidelines, sketches, barcode/UPC, product spec
                                sheets, photos of your product, labels you may like, etc.
                            </p>
                            <AssetsUploader uploadType="creative_vision" />
                        </>
                    )}

                    {isCustomArtwork && (
                        <>
                            <h3>Regulatory/Warning Information</h3>
                            <p>
                                Depending on the industry of your product, there may be information
                                you are required to include on your packaging. Select which industry
                                your product falls within.
                            </p>
                            <Select
                                label="Choose Your Industry *"
                                placeholder="Select an Industry *"
                                required
                                options={REGULATORY_INDUSTRY_TYPES.map(i => ({
                                    label: i.label,
                                    value: i.id
                                }))}
                                value={selectedIndustry?.id || ''}
                                onChange={v =>
                                    ctx.handleDesignOrderChange({product_industry_id: {$set: v}})
                                }
                            />
                            {/* TODO: make this conditional based on industry requirements */}
                            {selectedIndustry && (
                                <>
                                    <div className="hstack align-items-start">
                                        <div className="me-2">
                                            <Icon name="info" />
                                        </div>
                                        <div>
                                            <h6 className="m-0">{selectedIndustry.label}</h6>
                                            <p>
                                                Please check with the appropriate
                                                industry/government regulator for your product to
                                                ensure you have included all of the required
                                                information below for your label/carton. The Design
                                                Team will make suggestions based on research of
                                                FDA/governmental guidelines when creating your
                                                design. However, it is your full responsibility to
                                                review the artwork to ensure that your design
                                                complies with any FDA or government regulations for
                                                your product.
                                            </p>
                                        </div>
                                    </div>
                                    {selectedIndustry.id !== REGULATORY_INDUSTRY_NOTAPPLICABLE && (
                                        <Input
                                            rows={6}
                                            required
                                            label="Your product's regulatory information"
                                            placeholder="Enter your product's regulatory information *"
                                            value={designOrder.product_regulatory_information || ''}
                                            onChange={v =>
                                                ctx.handleDesignOrderChange({
                                                    product_regulatory_information: {$set: v}
                                                    //comments_customer: {$set: v}
                                                })
                                            }
                                        />
                                    )}
                                </>
                            )}
                        </>
                    )}
                </div>

                <div className="col-lg-4">
                    <div className=" pt-5 sticky-top">
                        <h4 className="mt-0">Order Summary</h4>
                        <OrderSummary />
                    </div>
                </div>
            </div>
        </div>
    );
}
